// src/API/Lesson/SentenceTranslationAPI.tsx
import apiClient from "../../services/apiClient";
import { mobileAppUri, dataLoaderUri } from "../API";
// Assuming ReconstructExerciseDBRecord is exported from its definition location
// If it's in VocabularySentenceGenerator.tsx, it needs to be exported from there,
// or ideally moved to a shared types file (e.g., src/API/types.ts)
import { ReconstructExerciseDBRecord } from "../../Lesson/SentenceTranslation/SentenceTranslation"; // Adjust path as needed

interface ApiReconstructExercisesResponseWrapper {
  items: ReconstructExerciseDBRecord[];
  // Include other potential wrapper properties like count, nextPageToken, etc.
}

/**
 * Fetches existing reconstruct exercises for a given section.
 *
 * @param sectionName The name of the section to fetch exercises for.
 * @returns A promise that resolves to an array of ReconstructExerciseDBRecord objects.
 */
export async function fetchExistingReconstructExercises(
  sectionName: string
): Promise<ReconstructExerciseDBRecord[]> {
  if (!sectionName) {
    console.warn(
      "fetchExistingReconstructExercises called without a sectionName."
    );
    return [];
  }

  if (!mobileAppUri) {
    console.error("API base URI (mobileAppUri) is not configured in API.ts.");
    throw new Error("API base URI (mobileAppUri) is not configured.");
  }

  const encodedSectionName = encodeURIComponent(sectionName);
  // Assuming this is the endpoint for reconstruct exercises, similar to practice-sentences
  const url = `${mobileAppUri}/api/v1/reconstruct-exercises?section=${encodedSectionName}`;

  console.log(
    "Attempting to fetch existing reconstruct exercises from URL:",
    url
  );

  try {
    const responseData = await apiClient.get(`${mobileAppUri}/api/v1/reconstruct-exercises`, {
      params: { section: sectionName }
    });

    if (responseData && Array.isArray(responseData.items)) {
      console.log(
        `Successfully fetched ${responseData.items.length} existing reconstruct exercises for section "${sectionName}".`
      );
      return responseData.items;
    } else {
      console.warn(
        `API response for reconstruct exercises (section "${sectionName}") did not contain an 'items' array or was not in the expected format:`,
        responseData
      );
      return [];
    }
  } catch (error) {
    console.error(
      `Error in fetchExistingReconstructExercises for section "${sectionName}":`,
      error
    );
    throw error;
  }
}

/**
 * Creates a new reconstruct exercise in the database.
 * @param exerciseData The full object for the new exercise.
 * @returns A promise that resolves to the API response (e.g., { message: "..." }).
 */
export async function createReconstructExercise(
  exerciseData: ReconstructExerciseDBRecord
): Promise<any> {
  // Replace 'any' with a more specific response type if known
  if (!dataLoaderUri) {
    console.error(
      "Data loader URI (dataLoaderUri) is not configured in API.ts."
    );
    throw new Error("Data loader URI (dataLoaderUri) is not configured.");
  }
  const url = `${dataLoaderUri}/api/v1/reconstruct-exercises`;
  console.log("POSTing new reconstruct exercise to:", url, exerciseData);

  try {
    const responseData = await apiClient.post(url, exerciseData);
    console.log("Successfully created reconstruct exercise:", responseData);
    return responseData;
  } catch (error) {
    console.error("Error in createReconstructExercise:", error);
    throw error;
  }
}

/**
 * Updates an existing reconstruct exercise.
 * The primary key for a reconstruct exercise is likely a composite of sectionId and exerciseId.
 * @param sectionId The partition key (e.g., section name or ID).
 * @param exerciseId The sort key (e.g., unique ID of the exercise).
 * @param updateData An object containing only the fields to be updated.
 * @returns A promise that resolves to the API response (e.g., { message: "..." }).
 */
export async function updateReconstructExercise(
  sectionId: string, // This would be ReconstructExerciseDBRecord.sectionId
  exerciseId: string, // This would be ReconstructExerciseDBRecord.exerciseId
  updateData: Partial<ReconstructExerciseDBRecord>
): Promise<any> {
  // Replace 'any' with a more specific response type if known
  if (!dataLoaderUri) {
    console.error(
      "Data loader URI (dataLoaderUri) is not configured in API.ts."
    );
    throw new Error("Data loader URI (dataLoaderUri) is not configured.");
  }
  // Ensure sectionId and exerciseId are properly encoded if they can contain special characters
  const encodedSectionId = encodeURIComponent(sectionId);
  const encodedExerciseId = encodeURIComponent(exerciseId);
  const url = `${dataLoaderUri}/api/v1/reconstruct-exercises/${encodedSectionId}/${encodedExerciseId}`;
  console.log(`PUTing update for reconstruct exercise to: ${url}`, updateData);

  try {
    const responseData = await apiClient.put(url, updateData);
    console.log(
      `Successfully updated reconstruct exercise ${sectionId}/${exerciseId}:`,
      responseData
    );
    return responseData;
  } catch (error) {
    console.error(
      `Error in updateReconstructExercise for ${sectionId}/${exerciseId}:`,
      error
    );
    throw error;
  }
}

/**
 * Deletes a reconstruct exercise from the database.
 * @param sectionId The partition key (e.g., section name or ID).
 * @param exerciseId The sort key (e.g., unique ID of the exercise).
 * @returns A promise that resolves to the API response (e.g., { message: "..." }).
 */
export async function deleteReconstructExercise(
  sectionId: string, // This would be ReconstructExerciseDBRecord.sectionId
  exerciseId: string // This would be ReconstructExerciseDBRecord.exerciseId
): Promise<any> {
  // Replace 'any' with a more specific response type if known
  if (!dataLoaderUri) {
    console.error(
      "Data loader URI (dataLoaderUri) is not configured in API.ts."
    );
    throw new Error("Data loader URI (dataLoaderUri) is not configured.");
  }
  const encodedSectionId = encodeURIComponent(sectionId);
  const encodedExerciseId = encodeURIComponent(exerciseId);
  const url = `${dataLoaderUri}/api/v1/reconstruct-exercises/${encodedSectionId}/${encodedExerciseId}`;
  console.log(`DELETEing reconstruct exercise at: ${url}`);

  try {
    const responseData = await apiClient.delete(url);
    console.log(
      `Successfully deleted reconstruct exercise ${sectionId}/${exerciseId}:`,
      responseData
    );
    return responseData;
  } catch (error) {
    console.error(
      `Error in deleteReconstructExercise for ${sectionId}/${exerciseId}:`,
      error
    );
    throw error;
  }
}
